import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';
import {
  AdminPermission,
  AddonPermission,
  BookingPermission,
  BannerPermission,
  ClassPermission,
  CampaignPermission,
  CustomerGroupPermission,
  LocationPermission,
  RolePermission,
  SpacePermission,
  TenantContactPermission,
  DashboardPermission,
  TenantGalleryPermission,
  TransactionPermission,
  UserPermission,
  TournamentPermission,
  TournamentDivisionPermission,
  TournamentTeamPermission,
  TournamentGroupPermission,
  TournamentMatchPermission,
  TripPermission,
} from '../../../../libs/role/src/lib/types/permission-type.type';
import { ModuleNameEnum } from '../../../../libs/core/src/lib/enums';
import { useMemo } from 'react';
import { useParams } from 'src/routes/hooks';
import { Label } from 'src/components/label';
import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

const iconify = (name: string) => <Iconify icon={name} />;

const ICONS = {
  analytics: iconify('material-symbols:analytics'),
  locations: iconify('material-symbols:map-outline'),
  spaces: iconify('icon-park-twotone:arena'),
  table: iconify('material-symbols:table'),
  calendar: iconify('uil:calender'),
  payment: iconify('material-symbols:payments'),
  event: iconify('material-symbols:event'),
  class: iconify('akar-icons:schedule'),
  campaign: iconify('mdi:bullhorn'),
  admin: iconify('material-symbols:admin-panel-settings-outline'),
  role: iconify('icon-park-twotone:permissions'),
  gallery: iconify('solar:gallery-wide-bold'),
  customer: iconify('mdi:users-outline'),
  contacts: iconify('mdi:contacts'),
  banner: iconify('ph:flag-banner'),
  tournament: iconify('mdi:tournament'),
  teams: iconify('ri:team-fill'),
  groups: iconify('material-symbols:group'),
  matches: iconify('ri:trophy-fill'),
  finance: iconify('mdi:finance'),
  trip: iconify('mdi:luggage'),
  addon: iconify('mdi:puzzle-outline'),
  customerGroup: iconify('solar:users-group-rounded-bold-duotone'),
};

// ----------------------------------------------------------------------

export const useDashboardNavData = () => {
  const { t } = useTranslate('navbar');
  return [
    {
      subheader: 'dashboard',
      module: ModuleNameEnum.DASHBOARD,
      items: [
        {
          title: 'booking',
          path: paths.overview.booking.root,
          module: ModuleNameEnum.BOOKINGS,
          icon: ICONS.analytics,
          permission: DashboardPermission.view,
          info: (
            <Label
              color="info"
              variant="inverted"
              startIcon={<Iconify icon="solar:bell-bing-bold-duotone" />}
            >
              {t('navbar.new')}
            </Label>
          ),
        },
        {
          title: 'user statistics',
          path: paths.overview.userStatistics.root,
          module: ModuleNameEnum.DASHBOARD,
          icon: ICONS.customer,
          permission: DashboardPermission.view,
        },
        // {
        //   title: 'finance',
        //   module: ModuleNameEnum.TRANSACTIONS,
        //   path: paths.overview.finance.root,
        //   icon: ICONS.finance,
        //   permission: DashboardPermission.view,
        // },
        // {
        //   title: 'location',
        //   module: ModuleNameEnum.LOCATIONS,
        //   path: paths.overview.location.root,
        //   icon: ICONS.locations,
        //   permission: DashboardPermission.view,
        // },
        // {
        //   title: 'customer',
        //   module: ModuleNameEnum.CUSTOMERS,
        //   path: paths.overview.customer.root,
        //   icon: ICONS.customer,
        //   permission: DashboardPermission.view,
        // },
      ],
    },
    {
      subheader: 'locations',
      items: [
        {
          title: 'locations',
          module: ModuleNameEnum.LOCATIONS,
          path: paths.locations.root,
          icon: ICONS.locations,
          permission: LocationPermission.list,
        },
        {
          title: 'spaces',
          module: ModuleNameEnum.SPACES,
          path: paths.spaces.root,
          icon: ICONS.spaces,
          permission: SpacePermission.list,
        },
      ],
    },
    {
      subheader: 'addons',
      items: [
        {
          title: 'addons',
          module: ModuleNameEnum.ADDONS,
          path: paths.addons.root,
          icon: ICONS.addon,
          permission: AddonPermission.list,
        },
      ],
    },
    {
      subheader: 'marketing',
      items: [
        {
          title: 'banners',
          module: ModuleNameEnum.BANNERS,
          path: paths.banners.root,
          icon: ICONS.banner,
          permission: BannerPermission.list,
        },
      ],
    },
    {
      subheader: 'bookings',
      items: [
        {
          title: 'table',
          module: ModuleNameEnum.BOOKINGS,
          path: paths.bookings.root,
          icon: ICONS.table,
          permission: BookingPermission.list,
        },
        {
          title: 'calendar',
          module: ModuleNameEnum.BOOKINGS,
          path: paths.bookings.calendar,
          icon: ICONS.calendar,
          permission: BookingPermission.calender,
        },
      ],
    },
    {
      subheader: 'finance',
      items: [
        {
          title: 'transactions',
          module: ModuleNameEnum.TRANSACTIONS,
          path: paths.transactions.root,
          icon: ICONS.payment,
          permission: TransactionPermission.list,
        },
      ],
    },

    {
      subheader: 'classes',
      items: [
        {
          title: 'list',
          module: ModuleNameEnum.CLASSES,
          path: paths.classes.list,
          icon: ICONS.table,
          permission: ClassPermission.list,
        },
        {
          title: 'calendar',
          module: ModuleNameEnum.CLASSES,
          path: paths.classes.root,
          icon: ICONS.class,
          permission: ClassPermission.list,
        },
      ],
    },
    {
      subheader: 'campaigns',
      items: [
        {
          title: 'campaigns',
          module: ModuleNameEnum.CAMPAIGNS,
          path: paths.campaigns.root,
          icon: ICONS.campaign,
          permission: CampaignPermission.list,
        },
      ],
    },
    {
      subheader: 'customers',
      items: [
        {
          title: 'customers',
          module: ModuleNameEnum.CUSTOMERS,
          path: paths.customers.root,
          icon: ICONS.customer,
          permission: UserPermission.list,
        },
        {
          title: 'customer groups',
          module: ModuleNameEnum.CUSTOMER_GROUPS,
          path: paths.customerGroups.root,
          icon: ICONS.customerGroup,
          permission: CustomerGroupPermission.list,
        },
      ],
    },
    {
      subheader: 'accessControlLevel',
      items: [
        {
          title: 'admins',
          module: ModuleNameEnum.AUTHENTICATION_ACL,
          path: paths.admins.root,
          icon: ICONS.admin,
          permission: AdminPermission.list,
        },
        {
          title: 'roles',
          module: ModuleNameEnum.AUTHENTICATION_ACL,
          path: paths.roles.root,
          icon: ICONS.role,
          permission: RolePermission.list,
        },
      ],
    },
    {
      subheader: 'settings',
      items: [
        {
          title: 'gallery',
          module: ModuleNameEnum.SETTINGS,
          path: paths.settings.gallery,
          icon: ICONS.gallery,
          permission: TenantGalleryPermission.list,
        },
        {
          title: 'contacts',
          module: ModuleNameEnum.SETTINGS,
          path: paths.settings.contacts,
          icon: ICONS.contacts,
          permission: TenantContactPermission.list,
        },
      ],
    },
    {
      subheader: 'tournaments',
      items: [
        {
          title: 'tournaments',
          module: ModuleNameEnum.TOURNAMENTS,
          path: paths.tournaments.list,
          icon: ICONS.tournament,
          permission: TournamentPermission.list,
        },
      ],
    },
    {
      subheader: 'trips',
      items: [
        {
          title: 'trips',
          module: ModuleNameEnum.TRIPS,
          path: paths.trips.list,
          icon: ICONS.trip,
          permission: TripPermission.list,
        },
      ],
    },
  ];
};

export const useDivisionNavData = () => {
  const { id = '', divisionId = '' } = useParams();
  const hasDivisionId = divisionId && divisionId !== '';
  const tournamentId = Number(id);
  
  const divisionNavData = useMemo(
    () => [
      {
        subheader: '',
        items: [
          {
            title: 'overview',
            path: hasDivisionId
              ? paths.tournaments.divisions.details(id, divisionId)
              : paths.tournaments.details(tournamentId),
            icon: ICONS.tournament,
            permission: TournamentDivisionPermission.view,
            disabled: !hasDivisionId,
          },
          {
            title: 'teams',
            path: hasDivisionId
              ? paths.tournaments.divisions.teams.list(id, divisionId)
              : paths.tournaments.details(tournamentId),
            icon: ICONS.teams,
            permission: TournamentTeamPermission.list,
          },
          {
            title: 'groups',
            path: hasDivisionId
              ? paths.tournaments.divisions.groups.list(id, divisionId)
              : paths.tournaments.details(tournamentId),
            icon: ICONS.groups,
            permission: TournamentGroupPermission.list,
            disabled: !hasDivisionId,
          },
          {
            title: 'matches',
            path: hasDivisionId
              ? paths.tournaments.divisions.matches.list(id, divisionId)
              : paths.tournaments.details(tournamentId),
            icon: ICONS.matches,
            permission: TournamentMatchPermission.list,
            disabled: !hasDivisionId,
          },
        ],
      },
    ],
    [divisionId, id, hasDivisionId, tournamentId],
  );
  return { divisionNavData };
};
